import { useState, useEffect } from 'react';
import { Bell, BellOff, Loader2, Check, AlertCircle } from 'lucide-react';
import { 
  isPushSupported, 
  getNotificationPermission, 
  requestNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  isSubscribedToPush
} from '../services/pushNotifications';

/**
 * Notification Bell Button - For enabling/disabling push notifications
 * Can be placed in header or settings page
 */
export const NotificationBell = ({ variant = 'icon' }) => {
  const [supported, setSupported] = useState(true);
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const checkStatus = async () => {
      setSupported(isPushSupported());
      setPermission(getNotificationPermission());
      const isSub = await isSubscribedToPush();
      setSubscribed(isSub);
    };
    checkStatus();
  }, []);

  const handleToggle = async () => {
    setLoading(true);
    
    try {
      if (subscribed) {
        // Unsubscribe
        await unsubscribeFromPush();
        setSubscribed(false);
      } else {
        // Request permission first
        if (permission !== 'granted') {
          const result = await requestNotificationPermission();
          setPermission(result.permission || 'denied');
          if (!result.success) {
            setLoading(false);
            return;
          }
        }
        
        // Subscribe
        const result = await subscribeToPush();
        if (result.success) {
          setSubscribed(true);
        }
      }
    } catch (error) {
      console.error('Notification toggle error:', error);
    }
    
    setLoading(false);
  };

  if (!supported) return null;

  if (permission === 'denied') {
    return (
      <button
        disabled
        className="p-2 text-zinc-600 cursor-not-allowed"
        title="Notifications blocked - enable in browser settings"
      >
        <BellOff className="w-5 h-5" />
      </button>
    );
  }

  if (variant === 'icon') {
    return (
      <button
        onClick={handleToggle}
        disabled={loading}
        className={`p-2 transition-colors ${
          subscribed 
            ? 'text-orange-500 hover:text-orange-400' 
            : 'text-zinc-400 hover:text-white'
        }`}
        title={subscribed ? 'Notifications enabled' : 'Enable notifications'}
        data-testid="notification-bell"
      >
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : subscribed ? (
          <Bell className="w-5 h-5 fill-current" />
        ) : (
          <Bell className="w-5 h-5" />
        )}
      </button>
    );
  }

  // Full button variant
  return (
    <button
      onClick={handleToggle}
      disabled={loading}
      className={`flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase transition-colors ${
        subscribed 
          ? 'bg-orange-500/20 text-orange-500 border border-orange-500/30' 
          : 'bg-zinc-800 text-zinc-400 hover:text-white border border-zinc-700'
      }`}
      data-testid="notification-toggle"
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : subscribed ? (
        <Bell className="w-4 h-4 fill-current" />
      ) : (
        <Bell className="w-4 h-4" />
      )}
      {subscribed ? 'Notifications On' : 'Enable Notifications'}
    </button>
  );
};

/**
 * Notification Settings Panel - Full settings component
 */
export const NotificationSettings = () => {
  const [supported, setSupported] = useState(true);
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preferences, setPreferences] = useState({
    gameReminders: true,
    finalScores: true,
    breakingNews: true
  });

  useEffect(() => {
    const checkStatus = async () => {
      setSupported(isPushSupported());
      setPermission(getNotificationPermission());
      const isSub = await isSubscribedToPush();
      setSubscribed(isSub);
      
      // Load saved preferences
      const saved = localStorage.getItem('notification_preferences');
      if (saved) {
        setPreferences(JSON.parse(saved));
      }
    };
    checkStatus();
  }, []);

  const handleSubscribe = async () => {
    setLoading(true);
    
    if (permission !== 'granted') {
      const result = await requestNotificationPermission();
      setPermission(result.permission || 'denied');
      if (!result.success) {
        setLoading(false);
        return;
      }
    }
    
    const result = await subscribeToPush();
    if (result.success) {
      setSubscribed(true);
    }
    
    setLoading(false);
  };

  const handleUnsubscribe = async () => {
    setLoading(true);
    await unsubscribeFromPush();
    setSubscribed(false);
    setLoading(false);
  };

  const handlePreferenceChange = (key) => {
    const updated = { ...preferences, [key]: !preferences[key] };
    setPreferences(updated);
    localStorage.setItem('notification_preferences', JSON.stringify(updated));
  };

  if (!supported) {
    return (
      <div className="p-4 bg-zinc-900/50 border border-white/10">
        <div className="flex items-center gap-2 text-zinc-500">
          <AlertCircle className="w-5 h-5" />
          <span>Push notifications are not supported in this browser</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-zinc-900/50 border border-white/10 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-white font-bold">Push Notifications</h3>
          <p className="text-zinc-500 text-sm">Get alerts for games and updates</p>
        </div>
        
        {permission === 'denied' ? (
          <span className="text-red-500 text-sm flex items-center gap-1">
            <BellOff className="w-4 h-4" />
            Blocked
          </span>
        ) : subscribed ? (
          <button
            onClick={handleUnsubscribe}
            disabled={loading}
            className="px-4 py-2 bg-zinc-800 text-zinc-400 hover:text-white text-sm font-bold uppercase"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Disable'}
          </button>
        ) : (
          <button
            onClick={handleSubscribe}
            disabled={loading}
            className="px-4 py-2 bg-orange-500 text-black text-sm font-bold uppercase hover:bg-orange-600"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enable'}
          </button>
        )}
      </div>

      {subscribed && (
        <div className="space-y-3 pt-4 border-t border-white/10">
          <p className="text-xs text-zinc-500 uppercase font-bold">Notification Types</p>
          
          {[
            { key: 'gameReminders', label: 'Game Reminders', desc: '15 minutes before tipoff' },
            { key: 'finalScores', label: 'Final Scores', desc: 'When games end' },
            { key: 'breakingNews', label: 'Breaking News', desc: 'Important updates' }
          ].map(({ key, label, desc }) => (
            <label key={key} className="flex items-center justify-between cursor-pointer group">
              <div>
                <span className="text-white group-hover:text-orange-500 transition-colors">{label}</span>
                <p className="text-zinc-600 text-xs">{desc}</p>
              </div>
              <div 
                onClick={() => handlePreferenceChange(key)}
                className={`w-10 h-6 rounded-full relative transition-colors cursor-pointer ${
                  preferences[key] ? 'bg-orange-500' : 'bg-zinc-700'
                }`}
              >
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  preferences[key] ? 'left-5' : 'left-1'
                }`} />
              </div>
            </label>
          ))}
        </div>
      )}

      {permission === 'denied' && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 text-sm text-red-400">
          <p>Notifications are blocked. To enable:</p>
          <ol className="list-decimal list-inside mt-2 space-y-1 text-xs">
            <li>Click the lock icon in your browser's address bar</li>
            <li>Find "Notifications" and change to "Allow"</li>
            <li>Refresh the page</li>
          </ol>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
